import React, { useState, useEffect } from 'react';
import { X, Heart, Users, Loader2 } from 'lucide-react';
import { matchApi } from '../lib/api';

interface AutoMatchesModalProps {
  isOpen: boolean;
  onClose: () => void;
  eventId: string;
}

const AutoMatchesModal: React.FC<AutoMatchesModalProps> = ({ isOpen, onClose, eventId }) => {
  const [matches, setMatches] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (isOpen && eventId) {
      loadAutoMatches();
    }
  }, [isOpen, eventId]);

  const loadAutoMatches = async () => {
    try {
      setLoading(true);
      const allMatches = await matchApi.getByEventId(eventId);
      const heartlinkMatches = (allMatches || []).filter(
        (m: any) => m.source === 'heartlink' && m.mutual === true
      );
      setMatches(heartlinkMatches);
    } catch (err) {
      console.error('Error loading auto matches:', err);
      setMatches([]);
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-2xl w-full max-w-2xl max-h-[85vh] flex flex-col shadow-2xl">
        <div className="flex items-center justify-between p-6 border-b border-gray-100">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-rose-100 rounded-xl">
              <Heart className="h-5 w-5 text-rose-600 fill-rose-600" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-gray-900">Mostra Match</h2>
              <p className="text-sm text-gray-500">Interessi reciproci dalla landing page HeartLink</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-xl transition-colors"
          >
            <X className="h-5 w-5 text-gray-500" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-6">
          {loading ? (
            <div className="flex flex-col items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-rose-500 mb-3" />
              <p className="text-gray-500">Caricamento match...</p>
            </div>
          ) : matches.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12">
              <Users className="h-12 w-12 text-gray-300 mb-3" />
              <p className="text-gray-500 text-lg font-medium">Nessun match automatico</p>
              <p className="text-gray-400 text-sm mt-1">
                I match appariranno quando due partecipanti si selezioneranno a vicenda
              </p>
            </div>
          ) : (
            <div className="space-y-3">
              <p className="text-sm text-gray-500 mb-4">
                {matches.length} match {matches.length === 1 ? 'trovato' : 'trovati'}
              </p>
              {matches.map((match: any) => (
                <div
                  key={match.id}
                  className="flex items-center gap-4 p-4 bg-gradient-to-r from-rose-50 to-pink-50 rounded-xl border border-rose-100"
                >
                  <div className="flex-1 flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-gradient-to-br from-rose-400 to-pink-500 flex items-center justify-center text-white font-bold text-sm shrink-0">
                      {(match.participant1?.name?.split(' ')[0] || '?').charAt(0).toUpperCase()}
                    </div>
                    <div className="min-w-0">
                      <p className="font-semibold text-gray-900 truncate">
                        {match.participant1?.name?.split(' ')[0] || 'Sconosciuto'}
                      </p>
                      <p className="text-xs text-gray-500">{formatGender(match.participant1?.gender)}</p>
                    </div>
                  </div>

                  <div className="shrink-0">
                    <Heart className="h-5 w-5 text-rose-500 fill-rose-500" />
                  </div>

                  <div className="flex-1 flex items-center gap-3 justify-end">
                    <div className="min-w-0 text-right">
                      <p className="font-semibold text-gray-900 truncate">
                        {match.participant2?.name?.split(' ')[0] || 'Sconosciuto'}
                      </p>
                      <p className="text-xs text-gray-500">{formatGender(match.participant2?.gender)}</p>
                    </div>
                    <div className="w-10 h-10 rounded-full bg-gradient-to-br from-pink-400 to-rose-500 flex items-center justify-center text-white font-bold text-sm shrink-0">
                      {(match.participant2?.name?.split(' ')[0] || '?').charAt(0).toUpperCase()}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="p-6 border-t border-gray-100">
          <button
            onClick={onClose}
            className="w-full py-2.5 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-xl font-medium transition-colors"
          >
            Chiudi
          </button>
        </div>
      </div>
    </div>
  );
};

const formatGender = (gender: string | null | undefined): string => {
  if (!gender) return '';
  const map: Record<string, string> = {
    'uomo_etero': 'Uomo Etero',
    'donna_etero': 'Donna Etero',
    'uomo_gay': 'Uomo Gay',
    'donna_gay': 'Donna Gay',
    'uomo_bisex': 'Uomo Bisex',
    'donna_bisex': 'Donna Bisex',
  };
  return map[gender] || gender;
};

export default AutoMatchesModal;
